import type { WorldOrgResponse } from "@uzverse/contracts";

/** A valid ULID with a readable tail (`01HZ` + zeros + tag), for the in-browser mock world. */
const mid = (tag: string): string => `01HZ${"0".repeat(22 - tag.length)}${tag}`;

export const MOCK_IDS = {
  company: mid("C1"),
  building: mid("B1"),
  hub: mid("H1"),
  terrain: mid("T1"),
  plaza: mid("P1"),
  parcel: mid("P2"),
  floors: [mid("F1"), mid("F2"), mid("F3")],
  agents: [mid("A1"), mid("A2"), mid("A3"), mid("A4")],
  /** Uzmoon: the first real company, a plaza tower with six themed floors. */
  uzmoon: mid("C2"),
  uzmoonBuilding: mid("B2"),
  uzmoonParcel: mid("P3"),
  uzmoonFloors: [mid("G0"), mid("G1"), mid("G2"), mid("G3"), mid("G4"), mid("G5")],
  uzmoonAgents: [mid("U1"), mid("U2"), mid("U3"), mid("U4"), mid("U5"), mid("U6"), mid("U7")],
} as const;

/** The Uzmoon brand as the world service delivers it (logos are served from `public/world/brand`). */
export const UZMOON_BRANDING = {
  name: "Uzmoon",
  logo: {
    light: "/brands/uzmoon/wordmark-black.png",
    dark: "/brands/uzmoon/wordmark-white.png",
    mark: "/brands/uzmoon/mark-black.png",
    icon: "/brands/uzmoon/icon-black.png",
  },
  colors: { primary: "#111111", onPrimary: "#ffffff", accent: "#e5e5e5" },
} as const;

export const UZMOON_STYLE = {
  preset: "plaza",
  entrance: "revolving",
  garage: true,
  flags: 2,
  canopy: true,
  rooftopSign: true,
  facade: "glass",
} as const;

/** Floors of Uzmoon, bottom to top: the lobby, then one themed floor per team. */
export const UZMOON_FLOORS = [
  { name: "Lobi", theme: "lobby" },
  { name: "Yazılım", theme: "software" },
  { name: "Stüdyo", theme: "studio" },
  { name: "Pazarlama", theme: "marketing" },
  { name: "Muhasebe", theme: "accounting" },
  { name: "Yönetim", theme: "leadership" },
] as const;

/** More people on the Uzmoon floors (the first floors seat a handful, the software floor a team). */
const TEAM_NAMES = ["Selin", "Baran", "Ece", "Tolga", "Defne", "Kerem", "Naz", "Onur", "Asli"];
const teamOf = (level: number, count: number) =>
  Array.from({ length: count }, (_, i) => ({
    id: mid(`W${level}${i}`),
    name: TEAM_NAMES[i % TEAM_NAMES.length]!,
    title: "Teammate",
  }));

/** Fixture for the mock backend (`VITE_UZ_MOCK=1`): one company, three floors. */
export const MOCK_WORLD_ORG = {
  companies: [
    {
      company: { id: MOCK_IDS.company, name: "Demo Café" },
      buildings: [
        {
          id: MOCK_IDS.building,
          name: "Main Building",
          footprint: { width: 20, depth: 20 },
          transform: { position: [0, 0, 0], rotation: [0, 0, 0, 1] },
          version: 1,
          floors: [
            {
              id: MOCK_IDS.floors[0],
              name: "Ground",
              level: 0,
              lead: { id: MOCK_IDS.agents[0], name: "Ada", title: "Operations lead" },
              workers: [],
            },
            {
              id: MOCK_IDS.floors[1],
              name: "Engineering",
              level: 1,
              lead: { id: MOCK_IDS.agents[1], name: "Linus", title: "Engineering lead" },
              workers: [
                { id: MOCK_IDS.agents[2], name: "Grace", title: "Web developer" },
                { id: MOCK_IDS.agents[3], name: "Ken" },
              ],
            },
            {
              id: MOCK_IDS.floors[2],
              name: "Marketing",
              level: 2,
              lead: null,
              workers: [],
            },
          ],
        },
      ],
    },
    {
      company: { id: MOCK_IDS.uzmoon, name: "Uzmoon" },
      buildings: [
        {
          id: MOCK_IDS.uzmoonBuilding,
          name: "Uzmoon Plaza",
          footprint: { width: 28, depth: 24 },
          transform: { position: [115, 0, 75], rotation: [0, 0, 0, 1] },
          version: 1,
          branding: UZMOON_BRANDING,
          style: UZMOON_STYLE,
          floors: UZMOON_FLOORS.map((f, level) => ({
            id: MOCK_IDS.uzmoonFloors[level],
            name: f.name,
            theme: f.theme,
            level,
            lead:
              level === 0
                ? null
                : { id: MOCK_IDS.uzmoonAgents[level], name: `${f.name} lead`, title: f.name },
            workers:
              level === 1
                ? [
                    { id: MOCK_IDS.uzmoonAgents[0], name: "Mira", title: "Backend" },
                    { id: MOCK_IDS.uzmoonAgents[6], name: "Deniz", title: "Frontend" },
                    ...teamOf(level, 3),
                  ]
                : level === 3
                  ? teamOf(level, 3)
                  : level === 5
                    ? teamOf(level, 3)
                    : [],
          })),
        },
      ],
    },
  ],
} as unknown as WorldOrgResponse;
